// ── <BugReportModal> — "Send feedback" ──
//
// One form for both kinds of feedback: a Problem (description + optional steps) or an Idea
// (description). Up to MAX_BUG_SCREENSHOTS images can be attached — captured on demand, pasted
// (anywhere, while the form is open), dropped onto the form or picked from disk — and are
// previewed as thumbnails. Opened from the Account panel's "Send feedback", the tour's
// post-forecast note, the keyboard shortcut and the three-finger tap (hooks/useBugReportTriggers.js).
//
// The component is GATED — the caller (App.jsx) renders it only while `bugOpen` is true.
// Handlers used elsewhere in AppInner are passed in, not duplicated:
//   submitBug         — submits the report (hooks/useBugReport.js)
//   captureScreenshot — captures a screenshot on demand and appends it
//   attachImageFiles  — (files, source) appends pasted / dropped / picked images
//   removeScreenshot  — (index) drops one attached image
//
// Runtime values come from `ctx`:
//   bugMode ("bug" | "feature"), setBugMode, bugDesc, setBugDesc, bugSteps, setBugSteps,
//   bugScreenshots (data URLs, in order), bugAttaching (images still decoding), bugSubmitting, bugCapturing,
//   bugHidden (hidden, not unmounted, during capture), setBugOpen, submitBug,
//   captureScreenshot, attachImageFiles, removeScreenshot.

import React from "react";
import { C } from "../lib/colors.js";
import { ModalShell, CloseButton } from "./Panels.jsx";
import { ui } from "../lib/telemetry/instrument.js";
import SegmentedControl from "./SegmentedControl.jsx";
import { Button } from "./ui/Primitives.jsx";
import { IconClose, IconMonitor, IconPlus } from "./ui/Icons.jsx";
import { product as brandProduct } from "../branding.js";
import { MAX_BUG_SCREENSHOTS, imageFilesFrom } from "../lib/bugReportImages.js";
import { bugReportShortcutLabel } from "../lib/bugReportTriggers.js";

const fieldLabel = {
  display: "block",
  fontSize: 13,
  fontWeight: 500,
  color: C.white,
  marginBottom: 6,
};

const textarea = {
  width: "100%",
  background: C.inputBg,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  color: C.white,
  fontSize: 13,
  lineHeight: 1.5,
  padding: "8px 10px",
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

export default function BugReportModal({ ctx }) {
  const {
    bugMode,
    setBugMode,
    bugDesc,
    setBugDesc,
    bugSteps,
    setBugSteps,
    bugScreenshots,
    bugAttaching,
    attachImageFiles,
    removeScreenshot,
    bugSubmitting,
    bugCapturing,
    bugHidden,
    setBugOpen,
    submitBug,
    captureScreenshot,
  } = ctx;

  const isIdea = bugMode === "feature";
  // Send waits for images still decoding, or the report would leave without them.
  const canSubmit = !!bugDesc.trim() && !bugSubmitting && !bugAttaching;
  return (
    <ModalShell
      onClose={() => setBugOpen(false)}
      surface="bug-report"
      // Above every app dialog and the listen player (1000), since the shortcut and the tap open
      // it over whatever is showing; below the ToS gate, the error screen and toasts.
      zIndex={1100}
      backdropStyle={bugHidden ? { visibility: "hidden" } : undefined}
      panelStyle={{
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        boxShadow: C.shadowBox,
        padding: 0,
        width: 520,
        maxWidth: "92vw",
        maxHeight: "85vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        marginTop: "8vh",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", padding: "20px 24px 8px", flexShrink: 0 }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C.white, letterSpacing: 0 }}>
          Send feedback
        </h2>
        <div style={{ flex: 1 }} />
        <CloseButton
          onClick={() => setBugOpen(false)}
          style={{ width: 32, height: 32, borderRadius: 8, fontSize: 18, color: C.gray }}
        />
      </div>
      {/* The form scrolls; the footer stays pinned so Send is always in reach. */}
      <div
        style={{
          padding: "12px 24px 4px",
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <SegmentedControl
          label="Kind of feedback"
          block
          value={bugMode}
          onChange={setBugMode}
          options={[
            { value: "bug", label: "Problem", ui: ui("bug-report.mode", "bug") },
            { value: "feature", label: "Idea", ui: ui("bug-report.mode", "feature") },
          ]}
        />
        <div>
          <label htmlFor="feedback-description" style={fieldLabel}>
            {isIdea ? "Your idea" : "What happened?"}
          </label>
          <textarea
            id="feedback-description"
            {...ui("bug-report.description")}
            autoFocus
            value={bugDesc}
            onChange={(e) => setBugDesc(e.target.value)}
            placeholder={
              isIdea
                ? `What would you like ${brandProduct.prose} to do, and why?`
                : "What did you expect, and what happened instead?"
            }
            style={{ ...textarea, minHeight: 120 }}
          />
        </div>
        {!isIdea && (
          <div>
            <label htmlFor="feedback-steps" style={fieldLabel}>
              Steps to reproduce <span style={{ color: C.gray, fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              id="feedback-steps"
              {...ui("bug-report.steps")}
              value={bugSteps}
              onChange={(e) => setBugSteps(e.target.value)}
              placeholder={"1. Open…\n2. Click…\n3. See…"}
              style={{ ...textarea, height: 76 }}
            />
          </div>
        )}
        <BugScreenshots
          shots={bugScreenshots}
          capturing={bugCapturing}
          onCapture={captureScreenshot}
          onAttachFiles={attachImageFiles}
          onRemove={removeScreenshot}
        />
      </div>
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 8,
          padding: "16px 24px",
          marginTop: 12,
          borderTop: `1px solid ${C.border}`,
        }}
      >
        <Button
          variant="ghost"
          data-popup-cancel=""
          {...ui("bug-report.cancel")}
          onClick={() => setBugOpen(false)}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          data-popup-primary=""
          {...ui("bug-report.submit")}
          disabled={!canSubmit}
          onClick={submitBug}
        >
          {bugSubmitting ? "Sending…" : bugAttaching ? "Attaching…" : "Send"}
        </Button>
      </div>
    </ModalShell>
  );
}

/**
 * The report's attached images: thumbnails (each with a remove ✕), "Capture screen" and
 * "Attach image" buttons, and a drop target around them. A paste anywhere while the form is
 * open is handled by useBugReport.
 */
function BugScreenshots({ shots, capturing, onCapture, onAttachFiles, onRemove }) {
  const [dragging, setDragging] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const full = shots.length >= MAX_BUG_SCREENSHOTS;
  const shortcut = bugReportShortcutLabel();
  const paste = shortcut === "⌃⌥B" ? "⌘V" : "Ctrl+V";
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  return (
    <div
      {...ui("bug-report.screenshot-drop")}
      onDragOver={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        setDragging(false);
        if (!hasFiles(e)) return;
        // Any dropped file, image or not: the browser would otherwise open it, losing the form.
        e.preventDefault();
        const files = imageFilesFrom(e.dataTransfer);
        if (files.length > 0) onAttachFiles(files, "drop");
      }}
      style={{
        borderRadius: 10,
        outline: dragging ? `2px dashed ${C.accent}` : "none",
        outlineOffset: 4,
      }}
    >
      {shots.length > 0 && (
        <>
          <span style={fieldLabel}>
            Screenshots{" "}
            <span style={{ color: C.gray, fontWeight: 400 }}>
              ({shots.length}/{MAX_BUG_SCREENSHOTS})
            </span>
          </span>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 8,
              marginBottom: 10,
            }}
          >
            {shots.map((src, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img
                  src={src}
                  alt={`Screenshot ${i + 1}`}
                  style={{
                    display: "block",
                    width: "100%",
                    height: 90,
                    objectFit: "cover",
                    objectPosition: "top",
                    borderRadius: 8,
                    border: `1px solid ${C.border}`,
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<IconClose />}
                  {...ui("bug-report.screenshot-remove", String(i + 1))}
                  onClick={() => onRemove(i)}
                  title="Remove screenshot"
                  aria-label={`Remove screenshot ${i + 1}`}
                  style={{ position: "absolute", top: 4, right: 4, background: C.bg, padding: 4 }}
                />
              </div>
            ))}
          </div>
        </>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button
          size="sm"
          icon={<IconMonitor />}
          {...ui("bug-report.screenshot")}
          onClick={onCapture}
          disabled={capturing || full}
        >
          {capturing ? "Capturing…" : "Capture screen"}
        </Button>
        <Button
          size="sm"
          icon={<IconPlus />}
          {...ui("bug-report.screenshot-attach")}
          onClick={() => fileInputRef.current?.click()}
          disabled={full}
        >
          Attach image
        </Button>
        <input
          {...ui("bug-report.screenshot-attach")}
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            onAttachFiles(e.target.files, "file");
            e.target.value = "";
          }}
        />
      </div>
      <div style={{ fontSize: 12, color: C.gray, marginTop: 6 }}>
        {full
          ? `That's the most screenshots one report can carry (${MAX_BUG_SCREENSHOTS}).`
          : `You can also paste (${paste}) or drop images here.`}{" "}
        Open this form from anywhere with{" "}
        <kbd style={{ fontFamily: "inherit", whiteSpace: "nowrap" }}>{shortcut}</kbd>.
      </div>
    </div>
  );
}
